import { useCallback, useState } from 'react'

import { RequestLog } from '@engine/models'

import { AdminListLayout } from '#components/AdminListLayout'
import { CopyButton } from '#components/CopyButton.js'
import { CopyServiceRequestButton } from '#components/CopyServiceRequestButton.js'
import {
  RequestLogFilters,
  useRequestLogFilters,
} from '#components/RequestLogFilters.js'
import { Slider } from '#components/Slider.js'
import { useInfiniteAdminResource } from '#hooks/useInfiniteAdminResource'
import { useIntersectionObserver } from '#hooks/useIntersectionObserver'

import { QuoteDetails } from './QuoteDetails.jsx'
import { QuoteList } from './QuoteList.jsx'

const QuoteView = () => {
  const [selectedQuote, setSelectedQuote] = useState<RequestLog | null>(null)
  const { filters, localFilters, setLocalFilters } = useRequestLogFilters()

  const {
    items: quotes,
    isLoading,
    isFetchingNextPage,
    hasNextPage,
    error,
    refresh,
    fetchNextPage,
  } = useInfiniteAdminResource<RequestLog>('request-logs', {
    type: 'rates',
    ...filters,
    limit: 50,
  })

  // Intersection observer to trigger loading more items
  const handleLoadMore = useCallback(() => {
    if (!isFetchingNextPage && hasNextPage) {
      fetchNextPage()
    }
  }, [isFetchingNextPage, hasNextPage, fetchNextPage])

  const sentinelRef = useIntersectionObserver(handleLoadMore, {
    enabled: hasNextPage && !isFetchingNextPage,
    threshold: 0.1,
  })

  const handleQuoteSelect = useCallback((quote: RequestLog) => {
    setSelectedQuote(quote)
  }, [])

  const handleCloseDetails = useCallback(() => {
    setSelectedQuote(null)
  }, [])

  const sliderTitle = (
    <div className="flex items-center gap-4">
      <span>Volume Quote Details</span>
      {selectedQuote && (
        <>
          <CopyButton
            textToCopy={`${window.location.protocol}//${window.location.host}${window.location.pathname}?requestId=${selectedQuote.requestId}`}
          />
          {selectedQuote.tenantRequest?.content && (
            <CopyServiceRequestButton log={selectedQuote} />
          )}
        </>
      )}
    </div>
  )

  return (
    <>
      <AdminListLayout
        title="Quotes"
        isLoading={isLoading}
        error={error}
        onRefresh={refresh}
        emptyMessage="No quotes found"
        filters={
          <RequestLogFilters
            isLoading={isLoading}
            onRefresh={refresh}
            localFilters={localFilters}
            setLocalFilters={setLocalFilters}
            statuses={['ok', 'error']}
            showQuoteType
            errorTypes={[
              'authentication',
              'external',
              'internal',
              'unknown',
              'validation',
            ]}
          />
        }
        listContent={
          quotes.length > 0 ? (
            <QuoteList
              quotes={quotes}
              selectedId={selectedQuote?._id}
              onSelect={handleQuoteSelect}
              infiniteScrollRef={sentinelRef}
              isFetchingMore={isFetchingNextPage}
            />
          ) : null
        }
      />

      <Slider
        isOpen={!!selectedQuote}
        onClose={handleCloseDetails}
        title={sliderTitle}
      >
        <QuoteDetails quote={selectedQuote} />
      </Slider>
    </>
  )
}

export { QuoteView }
